import { ArrowRight, Flower2, HeartHandshake, Phone, Truck, Users } from "lucide-react";
import { motion } from "framer-motion";
import { Seo } from "../components/Seo";
import { ButtonLink, Eyebrow, GhostLink, MaskedLines, Reveal, EASE } from "../components/ui-bits";
import { CTABand, Marquee, SectionHeading } from "../components/PageSections";
import { SITE, TOWNS } from "../data/site";

const TRUST = [
  { icon: Truck, title: "Local Delivery", text: "Coventry & Surrounding Areas" },
  { icon: Flower2, title: "Fresh, Quality Flowers", text: "Expertly Designed In-House" },
  { icon: Users, title: "Family Owned", text: "Since 1974" },
  { icon: HeartHandshake, title: "Personal Service", text: "For Every Occasion" },
];

const CATEGORIES = [
  {
    title: "Everyday Flowers",
    blurb: "Birthdays, anniversaries and just because.",
    cta: "Shop Now",
    href: "/occasions",
    testid: "category-card-everyday",
    image: "/images/home-cat-everyday.webp",
    imageAlt: "Everyday flower arrangement for birthdays and anniversaries",
  },
  {
    title: "Holidays & Seasonal",
    blurb: "Valentine's, Mother's Day, Thanksgiving & Christmas.",
    cta: "Explore",
    href: "/seasonal",
    testid: "category-card-seasonal",
    image: "/images/home-cat-holidays.webp",
    imageAlt: "Seasonal holiday centerpiece designed at JIF Flowers",
  },
  {
    title: "Sympathy Flowers",
    blurb: "Thoughtful tributes with compassion and care.",
    cta: "View Options",
    href: "/sympathy-funeral-flowers",
    testid: "category-card-sympathy",
    image: "/images/home-cat-sympathy.webp",
    imageAlt: "Elegant white sympathy tribute arrangement",
  },
  {
    title: "Plants & Gifts",
    blurb: "Beautiful plants and unique gifts for every occasion.",
    cta: "Shop Now",
    href: "/plants-gifts",
    testid: "category-card-plants",
    image: "/images/home-cat-plants.webp",
    imageAlt: "Green and blooming plants at JIF Flowers",
  },
];

const FEATURED = [
  {
    src: "/images/shop-1.webp",
    alt: "Green and blooming plants displayed in the JIF Flowers shop, Coventry RI",
  },
  {
    src: "/images/shop-2.webp",
    alt: "Coral and pink rose bouquet fresh from the JIF design bench",
  },
  {
    src: "/images/shop-3.webp",
    alt: "Pink rose vase arrangement hand-delivered to a Coventry home",
  },
  {
    src: "/images/shop-4.webp",
    alt: "Colorful hand-tied bouquet with roses and seasonal blooms by JIF Flowers",
  },
];

function HomeHero() {
  return (
    <section data-testid="home-hero" className="relative flex min-h-[88vh] items-center overflow-hidden bg-aubergine">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1572454591674-2739f30d8c40?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Lush bouquet of garden roses and seasonal blooms at JIF Flowers, Coventry RI"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </div>
      <div
        className="absolute inset-0 bg-gradient-to-r from-[#241019]/90 via-plum/60 to-plum/15"
        aria-hidden="true"
      />
      <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-36 sm:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
          <Eyebrow light>Family Owned Since 1974</Eyebrow>
        </motion.div>
        <h1
          data-testid="hero-headline"
          className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ivory sm:text-5xl lg:text-6xl"
        >
          <MaskedLines lines={["Beautiful Flowers.", { t: "Meaningful Moments." }]} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-6 max-w-xl text-[17px] leading-relaxed text-ivory/90 sm:text-[18px]"
        >
          At JIF – Jack Iannotti Flowers, we create fresh, beautiful arrangements with a personal touch. Proudly
          serving Coventry, Rhode Island and surrounding communities for over 50 years.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <ButtonLink href="/occasions" color="plum" testid="shop-flowers-button">
            Shop Flowers <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </ButtonLink>
          <ButtonLink href="/sympathy-funeral-flowers" color="outline" testid="sympathy-button">
            Sympathy & Funeral
          </ButtonLink>
        </motion.div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="border-b border-gold/25 bg-white/60">
      <div className="mx-auto grid max-w-7xl gap-y-8 px-5 py-10 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
        {TRUST.map((t, i) => (
          <Reveal
            key={t.title}
            delay={i * 0.08}
            className={`flex items-center gap-4 px-2 lg:px-6 ${i > 0 ? "lg:border-l lg:border-gold/30" : ""}`}
          >
            <t.icon className="h-9 w-9 shrink-0 text-sage" strokeWidth={1.4} aria-hidden="true" />
            <div>
              <h3 className="text-[12px] font-bold uppercase tracking-[0.16em] text-plum">{t.title}</h3>
              <p className="mt-0.5 text-sm text-charcoal/70">{t.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Categories() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.href} delay={i * 0.08}>
            <a href={c.href} data-testid={c.testid} className="group block overflow-hidden shadow-md shadow-plum/10">
              <div className="overflow-hidden">
                <img
                  src={c.image}
                  alt={c.imageAlt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                />
              </div>
              <div className={`p-6 transition-colors duration-500 ${i % 2 === 0 ? "bg-plum" : "bg-sage"}`}>
                <h3 className="font-serif text-2xl font-medium leading-tight text-ivory">{c.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-ivory/85">{c.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-ivory">
                  {c.cta}
                  <ArrowRight
                    className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5"
                    aria-hidden="true"
                  />
                </span>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Featured() {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-20 pt-4 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <h2 className="font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl lg:text-[42px]">
            Fresh From the Shop
          </h2>
          <p className="mt-2 text-xs font-semibold uppercase tracking-eyebrow text-sage">
            Fresh Designs for Every Occasion
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <GhostLink href="/gallery" testid="view-all-flowers-link">
            View All Flowers
          </GhostLink>
        </Reveal>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-5 lg:grid-cols-4">
        {FEATURED.map((f, i) => (
          <Reveal key={f.src} delay={i * 0.08}>
            <a
              href={SITE.phoneHref}
              data-testid={`featured-arrangement-${i}`}
              className="group relative block overflow-hidden"
            >
              <img
                src={f.src}
                alt={f.alt}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 flex items-end justify-center bg-plum/0 pb-5 transition-colors duration-500 group-hover:bg-plum/60">
                <span className="flex translate-y-3 items-center gap-2 rounded-full bg-ivory px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-plum opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                  Call to Order
                </span>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-8">
        <p className="max-w-2xl text-[15px] leading-relaxed text-charcoal/75">
          Every arrangement is designed fresh to order in our Coventry shop — see something you love? Call{" "}
          <a
            href={SITE.phoneHref}
            className="font-bold text-plum underline decoration-gold/60 underline-offset-4 hover:text-berry"
            data-testid="featured-call-link"
          >
            {SITE.phone}
          </a>{" "}
          and describe it, and we'll take it from there.
        </p>
      </Reveal>
    </section>
  );
}

function Story() {
  return (
    <section className="border-y border-gold/25 bg-white/50" data-testid="story-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <img
            src="/images/since_1895_to_today.webp"
            alt="Quidnick Green Houses in 1895 beside the JIF – Jack Iannotti Flowers shop on Main Street today"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover shadow-xl shadow-plum/15"
          />
        </Reveal>
        <Reveal delay={0.12}>
          <h2 className="font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl lg:text-[42px]">
            Our Roots Run Deep — Since 1895
          </h2>
          <p className="mt-3 font-serif text-xl italic text-sage sm:text-2xl">
            Three Generations of Coventry. One Flower at a Time.
          </p>
          <p className="mt-6 text-[16px] leading-relaxed text-charcoal/85">
            Our family's love of flowers began in 1895, when my grandparents established Quidnick Green Houses right
            here in Coventry.
          </p>
          <p className="mt-4 text-[16px] leading-relaxed text-charcoal/85">
            My father, Jack Iannotti, the oldest of five brothers, grew up in the family greenhouse business. In 1974,
            he and my mother, Millie, decided to build something of their own, opening JIF – Jack Iannotti Flowers on
            Main Street.
          </p>
          <p className="mt-4 text-[16px] leading-relaxed text-charcoal/85">
            I'm Edward Iannotti, the middle of their three sons, and today I'm proud to carry on the business my
            parents built.
          </p>
          <p className="mt-4 text-[16px] leading-relaxed text-charcoal/85">
            For more than 50 years, JIF has been part of life's special moments, from weddings and celebrations to
            heartfelt goodbyes. We've had the privilege of serving generations of local families, and we believe those
            relationships are what make a neighborhood flower shop special.
          </p>
          <p className="mt-4 text-[16px] leading-relaxed text-charcoal/85">
            A lot has changed over the years, but our commitment to beautiful flowers, personal service, and treating
            customers like family never will.
          </p>
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-plum sm:text-2xl">
            Our roots are in Coventry. Our passion is flowers. And our family tradition continues to bloom.
          </p>
          <p className="mt-4 font-serif text-lg italic text-charcoal/70">— Edward Iannotti</p>
          <div className="mt-8">
            <ButtonLink href="/about" color="olive" testid="read-full-story-link">
              Read Our Full Story <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section data-testid="reviews-section" className="bg-sagetint">
      <div className="mx-auto max-w-3xl px-5 py-20 text-center sm:px-8">
        <Reveal>
          <h2 className="font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">
            What Our Customers Say
          </h2>
          <div className="gold-rule mx-auto mt-5 w-40" aria-hidden="true" />
          <p className="mx-auto mt-6 max-w-xl text-[17px] leading-relaxed text-charcoal/80">
            Our neighbors across Coventry and Kent County have trusted us with their most important days for over 50
            years. See our latest work and read what customers are saying on our Facebook page.
          </p>
          <div className="mt-8">
            <motion.a
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-facebook-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2.5 rounded-full border-2 border-sage px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] text-sage transition-colors duration-300 hover:bg-sage hover:text-ivory sm:text-sm"
            >
              Visit Us on Facebook
            </motion.a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DeliveryStrip() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <SectionHeading
        eyebrow="Hand-Delivered"
        title="Same-day flower delivery across Kent County"
        lede="Our own van, our own driver, every delivery confirmed. Same-day in most cases when you order before 3:00 p.m."
      />
      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap gap-3">
          {TOWNS.map((t) => (
            <a
              key={t.path}
              href={t.path}
              data-testid={`home-town-${t.slug}`}
              className="rounded-full border border-gold/60 px-5 py-2.5 text-sm font-bold uppercase tracking-[0.12em] text-sage transition-all duration-300 hover:border-plum hover:bg-plum hover:text-ivory"
            >
              {t.name}, RI
            </a>
          ))}
        </div>
        <div className="mt-8">
          <GhostLink href="/flower-delivery" testid="home-delivery-link">
            All Delivery Areas
          </GhostLink>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Florist Coventry RI | JIF – Jack Iannotti Flowers, Since 1974"
        description="JIF – Jack Iannotti Flowers: family-owned florist in Coventry, RI since 1974. Sympathy, holiday & everyday flowers, hand-delivered. Call (401) 822-4440."
      />
      <HomeHero />
      <TrustStrip />
      <Categories />
      <Featured />
      <Story />
      <Marquee />
      <Reviews />
      <DeliveryStrip />
      <section className="border-t border-gold/25 bg-ivory">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
          <Reveal>
            <Eyebrow align="center">Sympathy & Funeral</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">
              When words are hard, flowers help
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-charcoal/80">
              We work directly with area funeral homes so your tribute arrives on time and exactly as ordered. If
              you're not sure what to send, call — we'll guide you gently.
            </p>
            <div className="mt-7">
              <GhostLink href="/sympathy-funeral-flowers" testid="home-sympathy-link">
                Sympathy & Funeral Flowers
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>
      <CTABand title="Ready to send something beautiful?" />
    </>
  );
}
